import type { LeagueSummary } from "@softmax/observatory-client/types";
import { type GameDestinationContext } from "@softmax/web-addresses";
import { reservedRootSegments } from "@softmax/web-addresses/namespaces";

// Public catalog responses can omit `public`; only an explicit false is private.
export function isUnpublishedLeague(
  league: Readonly<Pick<LeagueSummary, "public">>,
): boolean {
  return league.public === false;
}

export const reserved = new Set(reservedRootSegments);

export function slugifyLeagueName(name: string): string {
  return name
    .normalize("NFKD")
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, "-")
    .replace(/^-+|-+$/g, "");
}

function coworldSegment(league: LeagueSummary): string {
  const coworld = league.game?.coworld_name ?? null;
  return (
    slugifyLeagueName(coworld ?? league.name) ||
    slugifyLeagueName(league.name) ||
    league.id
  );
}

export function leagueRoute(
  league: LeagueSummary,
): GameDestinationContext & { league: string } {
  const game = league.game.game_address;

  if (!game || !league.short_name)
    throw new Error(
      `Missing persisted website address for league ${league.id}`,
    );

  return { game, league: league.short_name };
}

export type CoworldGroup<T extends LeagueSummary> = {
  root: string;
  legacyRoot: string;
  members: [T, ...T[]];
};

export function groupCoworldLeagues<T extends LeagueSummary>(
  leagues: readonly T[],
): CoworldGroup<T>[] {
  const groups = new Map<string, CoworldGroup<T>>();
  const occupied = new Set<string>();

  for (const league of leagues) {
    const key = league.game.id;
    const group = groups.get(key);

    if (group) {
      group.members.push(league);
      continue;
    }

    const base = coworldSegment(league);
    let legacyRoot = base;

    for (
      let n = 2;
      reserved.has(legacyRoot) || occupied.has(legacyRoot);
      n += 1
    ) {
      legacyRoot = `${base}-${n}`;
    }

    occupied.add(legacyRoot);
    const game = league.game.game_address;

    if (!game) throw new Error(`Missing game address for league ${league.id}`);

    const root = game.slug;
    groups.set(key, { root, legacyRoot, members: [league] });
  }

  return Array.from(groups.values());
}

// A private default must not change the public front door for signed-in owners.
export function defaultLeague<T extends LeagueSummary>(
  members: readonly [T, ...T[]],
): T {
  const defaultId = members[0].game?.default_league_id ?? null;
  const hasPublished = members.some((league) => !isUnpublishedLeague(league));
  return (
    members.find(
      (league) =>
        league.id === defaultId &&
        (!hasPublished || !isUnpublishedLeague(league)),
    ) ?? members[0]
  );
}

export function legacyLeagueNames<T extends LeagueSummary>(
  leagues: readonly T[],
): Map<string, T> {
  const legacy = new Map<string, T>();

  for (const league of leagues) {
    const base = slugifyLeagueName(league.name) || league.id;
    let slug = base;

    for (let n = 2; reserved.has(slug) || legacy.has(slug); n += 1) {
      slug = `${base}-${n}`;
    }

    legacy.set(slug, league);
  }

  return legacy;
}
